import React, { useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import ScopeTable5500 from "./ScopeTable5500"; // optional: if you have a Scope dialog

export default function Fluke5500ACalculator() {
  const [functionType, setFunctionType] = useState("");
  const [subFunction, setSubFunction] = useState("");
  const [amplitude, setAmplitude] = useState("");
  const [ampUnit, setAmpUnit] = useState("");
  const [frequency, setFrequency] = useState("");
  const [scopeValue, setScopeValue] = useState("");
  const [calculatedUncertainty, setCalculatedUncertainty] = useState("");
  const [uncertaintyInRatio, setUncertaintyInRatio] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);

  const buttonWidth = "120px";
  const validateNumeric = (value) => /^-?(\d+)?(\.\d*)?$/.test(value);

  // Robust copy (works on HTTP and HTTPS)
  const handleCopy = (value) => {
    if (!value) return;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(value)
        .catch((err) => console.error("Clipboard write failed:", err));
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = value;
      textArea.style.position = "fixed";
      textArea.style.opacity = 0;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }

      document.body.removeChild(textArea);
    }
  };

  const resetResults = () => {
    setScopeValue("");
    setCalculatedUncertainty("");
    setUncertaintyInRatio("");
  };

  const handleCalculate = async () => {
    setErrorMessage("");
    resetResults();

    if (!amplitude) {
      setErrorMessage("Please enter amplitude!");
      return;
    }
    if (!validateNumeric(amplitude) || !validateNumeric(frequency)) {
      setErrorMessage("Please enter valid numeric values!");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        function: functionType || "",
        subFunction: subFunction || "",
        amplitude: amplitude || "",
        amplitudeUnit: ampUnit || "",
        frequency: frequency || "",
      });

      // Adjust endpoint name for your backend (example):
      const response = await fetch(
        //`http://localhost:8080/api/calculator/calculate5500?${params.toString()}`
        `/api/calculator/calculate5500?${params.toString()}`
      );

      if (!response.ok) throw new Error("Server error: " + response.status);

      const data = await response.json();

      if (data.message) {
        setErrorMessage(data.message);
        return;
      }

      setScopeValue(data.scopeValue || "");
      setCalculatedUncertainty(data.calculatedUncertainty || "");
      setUncertaintyInRatio(data.uncertaintyInRatio || "");
    } catch (err) {
      setErrorMessage("Failed to fetch calculation: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Stack spacing={2}>
        {/* Title */}
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontWeight: "bold",
            color: "primary.dark",
            textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
            mb: 2,
          }}
        >
          Coil 5500 Uncertainty Calculator
        </Typography>

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {/* Function */}
        <TextField
          select
          label="Function"
          value={functionType}
          onChange={(e) => {
            setFunctionType(e.target.value);
            resetResults();
          }}
          fullWidth
        >
          <MenuItem value="">(None)</MenuItem>
          <MenuItem value="Current AC">Current AC</MenuItem>
          <MenuItem value="Current DC">Current DC</MenuItem>
        </TextField>

        {/* Sub Function */}
        <TextField
          select
          label="Sub Function"
          value={subFunction}
          onChange={(e) => {
            setSubFunction(e.target.value);
            resetResults();
          }}
          fullWidth
        >
          <MenuItem value="">(None)</MenuItem>
          <MenuItem value="Non-Toroidal">Non-Toroidal</MenuItem>
          <MenuItem value="Toroidal">Toroidal</MenuItem>
        </TextField>

        {/* Scope / Range Button */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ width: buttonWidth }}
            onClick={() => setScopeOpen(true)}
          >
            Show Scope
          </Button>
        </Box>

        {/* Amplitude & Unit */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Amplitude"
            value={amplitude}
            onChange={(e) => {
              setAmplitude(e.target.value);
              resetResults();
            }}
            fullWidth
          />
          <TextField
            select
            label="Unit"
            value={ampUnit}
            onChange={(e) => {
              setAmpUnit(e.target.value);
              resetResults();
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="A">A</MenuItem>
          </TextField>
        </Box>

        {/* Frequency */}
        <TextField
          label="Frequency (Hz)"
          value={frequency}
          onChange={(e) => {
            setFrequency(e.target.value);
            resetResults();
          }}
          fullWidth
        />

        {/* Calculate button */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ width: buttonWidth }}
            onClick={handleCalculate}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Calculate"}
          </Button>
        </Box>

        {/* Results */}
        <Stack spacing={1}>
          <Typography>Allowed Scope Value: {scopeValue}</Typography>

          <Typography>Calculated Uncertainty: {calculatedUncertainty}</Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => handleCopy(calculatedUncertainty)}
            >
              Copy
            </Button>
          </Box>

          <Typography>Uncertainty in Ratio: {uncertaintyInRatio}</Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => handleCopy(uncertaintyInRatio)}
            >
              Copy
            </Button>
          </Box>
        </Stack>
      </Stack>

      {/* Scope Table Dialog */}
      <ScopeTable5500
        open={scopeOpen}
        onClose={() => setScopeOpen(false)}
        functionType={functionType}
        subFunction={subFunction}
      />
    </Paper>
  );
}